import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { FaTimes, FaClock, FaLink, FaExclamationCircle, FaHeading, FaAlignLeft } from 'react-icons/fa';
import { db } from '../../../firebase/config';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { useAuthStore } from '../../../auth/state';
import './EditMeetingModal.css';

function EditMeetingModal({ open, onClose, meeting, onSuccess }) {
  const { userProfile } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  
  // # Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meetDate: '',
    meetTime: '',
    meetingUrl: '',
    invited: []
  });

  // # Handle form changes
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setIsDirty(true);
    setError('');
  }, []);

  // # Initialize form with meeting data
  useEffect(() => {
    if (meeting) {
      try {
        const date = new Date(meeting.meet_time);
        if (isNaN(date.getTime())) {
          throw new Error('Invalid date');
        }

        setFormData({
          title: meeting.title || '',
          description: meeting.description || '',
          meetDate: date.toISOString().split('T')[0], // Get only the date part
          meetTime: date.toTimeString().slice(0, 5), // Get only HH:mm
          meetingUrl: meeting.host?.url || '',
          invited: meeting.invited || []
        });
        setIsDirty(false);
        setError('');
      } catch (err) {
        console.error('Error initializing form:', err);
        setError('Error loading meeting data. Please try again.');
      }
    }
  }, [meeting]);

  // # Form validation
  const validateForm = useCallback(() => {
    if (!formData.title.trim()) {
      throw new Error('Meeting title is required');
    }

    if (!formData.description.trim()) {
      throw new Error('Meeting description is required');
    }

    if (!formData.meetDate || !formData.meetTime) {
      throw new Error('Meeting date and time are required');
    }

    // # Combine date and time for validation
    const meetDateTime = new Date(`${formData.meetDate}T${formData.meetTime}`);
    if (isNaN(meetDateTime.getTime())) {
      throw new Error('Invalid meeting date/time');
    }

    if (meetDateTime < new Date()) {
      throw new Error('Meeting time cannot be in the past');
    }

    if (!formData.meetingUrl.trim()) {
      throw new Error('Meeting URL is required');
    }

    try {
      new URL(formData.meetingUrl);
    } catch {
      throw new Error('Invalid meeting URL');
    }
  }, [formData]);

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      validateForm();
      
      // # Combine date and time
      const meetDateTime = new Date(`${formData.meetDate}T${formData.meetTime}`);
      
      // # Update meeting data
      const updatedMeeting = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        meet_time: meetDateTime,
        host: {
          uid: userProfile.uid,
          url: formData.meetingUrl.trim()
        },
        invited: formData.invited,
        metadata: {
          ...meeting.metadata,
          last_edited_at: new Date().toISOString(),
          last_edited_by: userProfile.uid
        }
      };

      // # Update in Firestore
      const meetingsRef = doc(db, 'meetings', 'gen_meetings');
      
      await updateDoc(meetingsRef, {
        [meeting.id]: updatedMeeting
      });
      
      setIsDirty(false);
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error updating meeting:', error);
      setError(error.message || 'Failed to update meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // # Handle meeting deletion
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this meeting? This action cannot be undone.')) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const meetingsRef = doc(db, 'meetings', 'gen_meetings');
      
      // # Create updated document without the deleted meeting
      await updateDoc(meetingsRef, {
        [meeting.id]: deleteField()
      });

      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error deleting meeting:', error);
      setError(error.message || 'Failed to delete meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // # Handle modal close with unsaved changes
  const handleCloseAttempt = useCallback(() => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setError('');
        onClose?.();
      }
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  // # Prevent closing modal when clicking backdrop if submitting
  const handleBackdropClick = useCallback((e) => {
    if (!isSubmitting) {
      handleCloseAttempt();
    }
    e.stopPropagation();
  }, [isSubmitting, handleCloseAttempt]);

  return (
    <Dialog 
      open={open} 
      onClose={handleCloseAttempt}
      className="edit_meeting_modal_wrapper"
    >
      <div className="edit_meeting_modal_backdrop" onClick={handleBackdropClick} />
      
      <Dialog.Panel className="edit_meeting_modal">
        <div className="edit_meeting_modal_header">
          <Dialog.Title as="h2">Edit Meeting</Dialog.Title>
          <button 
            className="edit_meeting_modal_close"
            onClick={handleCloseAttempt}
            disabled={isSubmitting}
            title="Close modal"
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="edit_meeting_modal_form">
          <div className="edit_meeting_modal_field">
            <label htmlFor="title">
              <FaHeading /> Title
            </label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              placeholder="Enter meeting title"
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="edit_meeting_modal_field">
            <label htmlFor="description">
              <FaAlignLeft /> Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              required
              placeholder="Enter meeting description"
              disabled={isSubmitting}
              rows={4}
            />
          </div>

          <div className="edit_meeting_modal_datetime">
            <div className="edit_meeting_modal_field">
              <label htmlFor="meetDate">
                <FaClock /> Meeting Date
              </label>
              <input
                id="meetDate"
                type="date"
                value={formData.meetDate}
                onChange={(e) => handleChange('meetDate', e.target.value)}
                required
                min={new Date().toISOString().split('T')[0]}
                disabled={isSubmitting}
              />
            </div>

            <div className="edit_meeting_modal_field">
              <label htmlFor="meetTime">
                <FaClock /> Meeting Time
              </label>
              <input
                id="meetTime"
                type="time"
                value={formData.meetTime}
                onChange={(e) => handleChange('meetTime', e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="edit_meeting_modal_field">
            <label htmlFor="meetingUrl">
              <FaLink /> Meeting URL
            </label>
            <input
              id="meetingUrl"
              type="url"
              value={formData.meetingUrl}
              onChange={(e) => handleChange('meetingUrl', e.target.value)}
              required
              placeholder="https://zoom.us/..."
              disabled={isSubmitting}
              pattern="https?://.*"
            />
          </div>

          {error && (
            <div className="edit_meeting_modal_error" role="alert">
              <FaExclamationCircle />
              {error}
            </div>
          )}

          <div className="edit_meeting_modal_actions">
            <button
              type="button"
              onClick={handleDelete}
              className="edit_meeting_modal_delete"
              disabled={isSubmitting}
            >
              Delete Meeting
            </button>
            <div className="edit_meeting_modal_action_group">
              <button
                type="button"
                onClick={handleCloseAttempt}
                className="edit_meeting_modal_cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="edit_meeting_modal_submit"
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? 'Updating...' : 'Update Meeting'}
              </button>
            </div>
          </div>
        </form>
      </Dialog.Panel>
    </Dialog>
  );
}

export default EditMeetingModal; 